import { useEffect, useRef, useState } from "react";
import { HERO_ROTATION } from "@/lib/photos";
import ResponsiveImage from "./ResponsiveImage";

/**
 * HeroRotator — cross-fading hero background.
 *
 * P1E: only the first (LCP) slide is requested at startup. Inactive slides
 * mount JUST-IN-TIME — the next slide is prepared, and the cross-fade only
 * advances once that next image has ACTUALLY LOADED. If it is late on a slow
 * network the current slide stays visible (never a blank / black / partial
 * frame); the advance happens on the image's onLoad. Only the current slide
 * plus the next prepared slide are ever mounted. The first slide renders in
 * its final visible state (no transition/opacity gating) for immediate LCP.
 * LCP slide (idx 0) stays eager + fetchpriority=high and matches the preload.
 */
export default function HeroRotator({ interval = 6000 }) {
  const n = HERO_ROTATION.length;
  const [i, setI] = useState(0);
  const [mounted, setMounted] = useState(() => new Set([0]));
  const [loaded, setLoaded] = useState(() => new Set());

  const iRef = useRef(0);
  const loadedRef = useRef(loaded);
  const pendingTargetRef = useRef(null);
  useEffect(() => { iRef.current = i; }, [i]);
  useEffect(() => { loadedRef.current = loaded; }, [loaded]);

  const mount = (idx) => setMounted((p) => (p.has(idx) ? p : new Set(p).add(idx)));

  const handleLoaded = (idx) => {
    setLoaded((p) => (p.has(idx) ? p : new Set(p).add(idx)));
    // If we were waiting for this exact slide to become ready, advance now.
    if (pendingTargetRef.current === idx) {
      pendingTargetRef.current = null;
      setI(idx);
    }
  };

  // P1F: the next slide is NOT mounted at startup. Mounting slide 1 on first
  // render made its bytes (f293owpn ~59 KiB) compete with the initial LCP hero
  // on a slow mobile connection. It is now prepared JUST-IN-TIME — a bounded
  // lead BEFORE its scheduled rotation, derived from the rotation interval — so
  // it never lands in the initial LCP window. If it is not ready at the
  // rotation deadline the current slide stays visible and the advance happens
  // on its onLoad (no blank / black / partial frame).
  useEffect(() => {
    if (n <= 1) return;
    let idleId = null, startId = null, prepId = null, rotId = null;
    // Lead time before a rotation to begin loading the next slide. Bounded and
    // derived from the interval; keeps the fetch well outside the ~LCP window.
    const LEAD = Math.min(2200, Math.max(1200, Math.round(interval * 0.35)));

    const cycle = () => {
      const want = (iRef.current + 1) % n;
      // Begin loading the next slide only sufficiently before its rotation.
      prepId = setTimeout(() => mount(want), Math.max(0, interval - LEAD));
      rotId = setTimeout(() => {
        mount(want);
        if (loadedRef.current.has(want)) setI(want);
        else pendingTargetRef.current = want; // keep current visible; advance on onLoad
        cycle();
      }, interval);
    };

    const start = () => cycle();
    if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(start, { timeout: 3000 });
    } else {
      startId = setTimeout(start, 1500);
    }
    return () => {
      if (prepId) clearTimeout(prepId);
      if (rotId) clearTimeout(rotId);
      if (startId) clearTimeout(startId);
      if (idleId && typeof window !== "undefined" && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval, n]);

  return (
    <div className="absolute inset-0" data-testid="hero-rotator">
      {HERO_ROTATION.map((src, idx) => {
        if (!mounted.has(idx)) return null;
        const cls = idx === 0 ? "absolute inset-0" : "absolute inset-0 transition-opacity duration-[1400ms] ease-in-out";
        return (
          <div key={src} className={cls} style={{ opacity: idx === i ? 1 : 0 }}>
            <ResponsiveImage
              src={src}
              alt=""
              priority={idx === 0}
              loading={idx === 0 ? "eager" : "lazy"}
              maxWidth={1600}
              sizes="100vw"
              className="w-full h-full object-cover"
              onLoad={() => handleLoaded(idx)}
            />
          </div>
        );
      })}
      <div className="absolute inset-0 bg-gradient-to-br from-bronze-dark/70 via-bronze-dark/40 to-bronze-dark/85" />
      {/* Indicator */}
      <div className="absolute bottom-6 right-6 md:right-16 flex gap-2 z-20">
        {HERO_ROTATION.map((_, idx) => (
          <button
            key={idx}
            onClick={() => { mount(idx); if (loaded.has(idx) || idx === 0) setI(idx); else pendingTargetRef.current = idx; }}
            className={`h-1 transition-all ${idx === i ? "w-12 bg-copper-light" : "w-6 bg-ivory/40 hover:bg-ivory/70"}`}
            aria-label={`Show slide ${idx + 1}`}
            data-testid={`hero-slide-${idx}`}
          />
        ))}
      </div>
    </div>
  );
}
